/**
 * Copyright 2017 Google Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * @fileoverview Defines types for interacting with blob transfer tasks.
 */
import { InternalTaskState } from './implementation/taskenums';
import { Observer } from './implementation/observer';
import { TaskEvent, TaskState } from './implementation/taskenums';
import { UploadTaskSnapshot } from './tasksnapshot';
import * as fbsArgs from './implementation/args';
import * as fbsArray from './implementation/array';
import { async as fbsAsync } from './implementation/async';
import * as errors from './implementation/error';
import * as fbsPromiseimpl from './implementation/promise_external';
import * as fbsRequests from './implementation/requests';
import * as fbsTaskEnums from './implementation/taskenums';
import * as typeUtils from './implementation/type';
/**
 * Represents a blob being uploaded. Can be used to pause/resume/cancel the
 * upload and manage callbacks for various events.
 */
var UploadTask = /** @class */ (function () {
 /**
 * @param ref The firebaseStorage.Reference object this task came
 * from, untyped to avoid cyclic dependencies.
 * @param blob The blob to upload.
 */
 function UploadTask(ref, authWrapper, location, mappings, blob, metadata) {
 if (metadata === void 0) { metadata = null; }
 var _this = this;
 this.transferred_ = 0;
 this.needToFetchStatus_ = false;
 this.needToFetchMetadata_ = false;
 this.observers_ = [];
 this.error_ = null;
 this.uploadUrl_ = null;
 this.request_ = null;
 this.chunkMultiplier_ = 1;
 this.resolve_ = null;
 this.reject_ = null;
 this.ref_ = ref;
 this.authWrapper_ = authWrapper;
 this.location_ = location;
 this.blob_ = blob;
 this.metadata_ = metadata;
 this.mappings_ = mappings;
 this.resumable_ = this.shouldDoResumable_(this.blob_);
 this.state_ = InternalTaskState.RUNNING;
 this.errorHandler_ = function (error) {
 _this.request_ = null;
 _this.chunkMultiplier_ = 1;
 if (error.codeEquals(errors.Code.CANCELED)) {
 _this.needToFetchStatus_ = true;
 _this.completeTransitions_();
 }
 else {
 _this.error_ = error;
 _this.transition_(InternalTaskState.ERROR);
 }
 };
 this.metadataErrorHandler_ = function (error) {
 _this.request_ = null;
 if (error.codeEquals(errors.Code.CANCELED)) {
 _this.completeTransitions_();
 }
 else {
 _this.error_ = error;
 _this.transition_(InternalTaskState.ERROR);
 }
 };
 this.promise_ = fbsPromiseimpl.make(function (resolve, reject) {
 _this.resolve_ = resolve;
 _this.reject_ = reject;
 _this.start_();
 });
 // Prevent uncaught rejections on the internal promise from bubbling out
 // to the top level with a dummy handler.
 this.promise_.then(null, function () { });
 }
 UploadTask.prototype.makeProgressCallback_ = function () {
 var _this = this;
 var sizeBefore = this.transferred_;
 return function (loaded, total) {
 _this.updateProgress_(sizeBefore + loaded);
 };
 };
 UploadTask.prototype.shouldDoResumable_ = function (blob) {
 return blob.size() > 256 * 1024;
 };
 UploadTask.prototype.start_ = function () {
 if (this.state_ !== InternalTaskState.RUNNING) {
 // This can happen if someone pauses us in a resume callback, for example.
 return;
 }
 if (this.request_ !== null) {
 return;
 }
 if (this.resumable_) {
 if (this.uploadUrl_ === null) {
 this.createResumable_();
 }
 else {
 if (this.needToFetchStatus_) {
 this.fetchStatus_();
 }
 else {
 if (this.needToFetchMetadata_) {
 // Happens if we miss the metadata on upload completion.
 this.fetchMetadata_();
 }
 else {
 this.continueUpload_();
 }
 }
 }
 }
 else {
 this.oneShotUpload_();
 }
 };
 UploadTask.prototype.resolveToken_ = function (callback) {
 var _this = this;
 this.authWrapper_.getAuthToken().then(function (authToken) {
 switch (_this.state_) {
 case InternalTaskState.RUNNING:
 callback(authToken);
 break;
 case InternalTaskState.CANCELING:
 _this.transition_(InternalTaskState.CANCELED);
 break;
 case InternalTaskState.PAUSING:
 _this.transition_(InternalTaskState.PAUSED);
 break;
 default:
 }
 });
 };
 // TODO(andysoto): assert false
 UploadTask.prototype.createResumable_ = function () {
 var _this = this;
 this.resolveToken_(function (authToken) {
 var requestInfo = fbsRequests.createResumableUpload(_this.authWrapper_, _this.location_, _this.mappings_, _this.blob_, _this.metadata_);
 var createRequest = _this.authWrapper_.makeRequest(requestInfo, authToken);
 _this.request_ = createRequest;
 createRequest.getPromise().then(function (url) {
 _this.request_ = null;
 _this.uploadUrl_ = url;
 _this.needToFetchStatus_ = false;
 _this.completeTransitions_();
 }, _this.errorHandler_);
 });
 };
 UploadTask.prototype.fetchStatus_ = function () {
 var _this = this;
 // TODO(andysoto): assert(this.uploadUrl_ !== null);
 var url = this.uploadUrl_;
 this.resolveToken_(function (authToken) {
 var requestInfo = fbsRequests.getResumableUploadStatus(_this.authWrapper_, _this.location_, url, _this.blob_);
 var statusRequest = _this.authWrapper_.makeRequest(requestInfo, authToken);
 _this.request_ = statusRequest;
 statusRequest.getPromise().then(function (status) {
 status = status;
 _this.request_ = null;
 _this.updateProgress_(status.current);
 _this.needToFetchStatus_ = false;
 if (status.finalized) {
 _this.needToFetchMetadata_ = true;
 }
 _this.completeTransitions_();
 }, _this.errorHandler_);
 });
 };
 UploadTask.prototype.continueUpload_ = function () {
 var _this = this;
 var chunkSize = fbsRequests.resumableUploadChunkSize * this.chunkMultiplier_;
 var status = new fbsRequests.ResumableUploadStatus(this.transferred_, this.blob_.size());
 // TODO(andysoto): assert(this.uploadUrl_ !== null);
 var url = this.uploadUrl_;
 this.resolveToken_(function (authToken) {
 var requestInfo;
 try {
 requestInfo = fbsRequests.continueResumableUpload(_this.location_, _this.authWrapper_, url, _this.blob_, chunkSize, _this.mappings_, status, _this.makeProgressCallback_());
 }
 catch (e) {
 _this.error_ = e;
 _this.transition_(InternalTaskState.ERROR);
 return;
 }
 var uploadRequest = _this.authWrapper_.makeRequest(requestInfo, authToken);
 _this.request_ = uploadRequest;
 uploadRequest
 .getPromise()
 .then(function (newStatus) {
 _this.increaseMultiplier_();
 _this.request_ = null;
 _this.updateProgress_(newStatus.current);
 if (newStatus.finalized) {
 _this.metadata_ = newStatus.metadata;
 _this.transition_(InternalTaskState.SUCCESS);
 }
 else {
 _this.completeTransitions_();
 }
 }, _this.errorHandler_);
 });
 };
 UploadTask.prototype.increaseMultiplier_ = function () {
 var currentSize = fbsRequests.resumableUploadChunkSize * this.chunkMultiplier_;
 // Max chunk size is 32M.
 if (currentSize < 32 * 1024 * 1024) {
 this.chunkMultiplier_ *= 2;
 }
 };
 UploadTask.prototype.fetchMetadata_ = function () {
 var _this = this;
 this.resolveToken_(function (authToken) {
 var requestInfo = fbsRequests.getMetadata(_this.authWrapper_, _this.location_, _this.mappings_);
 var metadataRequest = _this.authWrapper_.makeRequest(requestInfo, authToken);
 _this.request_ = metadataRequest;
 metadataRequest.getPromise().then(function (metadata) {
 _this.request_ = null;
 _this.metadata_ = metadata;
 _this.transition_(InternalTaskState.SUCCESS);
 }, _this.metadataErrorHandler_);
 });
 };
 UploadTask.prototype.oneShotUpload_ = function () {
 var _this = this;
 this.resolveToken_(function (authToken) {
 var requestInfo = fbsRequests.multipartUpload(_this.authWrapper_, _this.location_, _this.mappings_, _this.blob_, _this.metadata_);
 var multipartRequest = _this.authWrapper_.makeRequest(requestInfo, authToken);
 _this.request_ = multipartRequest;
 multipartRequest.getPromise().then(function (metadata) {
 _this.request_ = null;
 _this.metadata_ = metadata;
 _this.updateProgress_(_this.blob_.size());
 _this.transition_(InternalTaskState.SUCCESS);
 }, _this.errorHandler_);
 });
 };
 UploadTask.prototype.updateProgress_ = function (transferred) {
 var old = this.transferred_;
 this.transferred_ = transferred;
 // A progress update can make the "transferred" value smaller (e.g. a
 // partial upload not completed by server, after which the "transferred"
 // value may reset to the value at the beginning of the request).
 if (this.transferred_ !== old) {
 this.notifyObservers_();
 }
 };
 UploadTask.prototype.transition_ = function (state) {
 if (this.state_ === state) {
 return;
 }
 switch (state) {
 case InternalTaskState.CANCELING:
 // TODO(andysoto):
 // assert(this.state_ === InternalTaskState.RUNNING ||
 // this.state_ === InternalTaskState.PAUSING);
 this.state_ = state;
 if (this.request_ !== null) {
 this.request_.cancel();
 }
 break;
 case InternalTaskState.PAUSING:
 // TODO(andysoto):
 // assert(this.state_ === InternalTaskState.RUNNING);
 this.state_ = state;
 if (this.request_ !== null) {
 this.request_.cancel();
 }
 break;
 case InternalTaskState.RUNNING:
 // TODO(andysoto):
 // assert(this.state_ === InternalTaskState.PAUSED ||
 // this.state_ === InternalTaskState.PAUSING);
 var wasPaused = this.state_ === InternalTaskState.PAUSED;
 this.state_ = state;
 if (wasPaused) {
 this.notifyObservers_();
 this.start_();
 }
 break;
 case InternalTaskState.PAUSED:
 // TODO(andysoto):
 // assert(this.state_ === InternalTaskState.PAUSING);
 this.state_ = state;
 this.notifyObservers_();
 break;
 case InternalTaskState.CANCELED:
 // TODO(andysoto):
 // assert(this.state_ === InternalTaskState.PAUSED ||
 // this.state_ === InternalTaskState.CANCELING);
 this.error_ = errors.canceled();
 this.state_ = state;
 this.notifyObservers_();
 break;
 case InternalTaskState.ERROR:
 // TODO(andysoto):
 // assert(this.state_ === InternalTaskState.RUNNING ||
 // this.state_ === InternalTaskState.PAUSING ||
 // this.state_ === InternalTaskState.CANCELING);
 this.state_ = state;
 this.notifyObservers_();
 break;
 case InternalTaskState.SUCCESS:
 // TODO(andysoto):
 // assert(this.state_ === InternalTaskState.RUNNING ||
 // this.state_ === InternalTaskState.PAUSING ||
 // this.state_ === InternalTaskState.CANCELING);
 this.state_ = state;
 this.notifyObservers_();
 break;
 }
 };
 UploadTask.prototype.completeTransitions_ = function () {
 switch (this.state_) {
 case InternalTaskState.PAUSING:
 this.transition_(InternalTaskState.PAUSED);
 break;
 case InternalTaskState.CANCELING:
 this.transition_(InternalTaskState.CANCELED);
 break;
 case InternalTaskState.RUNNING:
 this.start_();
 break;
 default:
 // TODO(andysoto): assert(false);
 break;
 }
 };
 Object.defineProperty(UploadTask.prototype, "snapshot", {
 get: function () {
 var externalState = fbsTaskEnums.taskStateFromInternalTaskState(this.state_);
 return new UploadTaskSnapshot(this.transferred_, this.blob_.size(), externalState, this.metadata_, this, this.ref_);
 },
 enumerable: true,
 configurable: true
 });
 /**
 * Adds a callback for an event.
 * @param type The type of event to listen for.
 */
 UploadTask.prototype.on = function (type, nextOrObserver, error, completed) {
 if (nextOrObserver === void 0) { nextOrObserver = undefined; }
 if (error === void 0) { error = undefined; }
 if (completed === void 0) { completed = undefined; }
 function typeValidator(_p) {
 if (type !== TaskEvent.STATE_CHANGED) {
 throw "Expected one of the event types: [" + TaskEvent.STATE_CHANGED + "].";
 }
 }
 var nextOrObserverMessage = 'Expected a function or an Object with one of ' +
 '`next`, `error`, `complete` properties.';
 var nextValidator = fbsArgs.nullFunctionSpec(true).validator;
 var observerValidator = fbsArgs.looseObjectSpec(null, true).validator;
 function nextOrObserverValidator(p) {
 try {
 nextValidator(p);
 return;
 }
 catch (e) { }
 try {
 observerValidator(p);
 var anyDefined = typeUtils.isJustDef(p['next']) ||
 typeUtils.isJustDef(p['error']) ||
 typeUtils.isJustDef(p['complete']);
 if (!anyDefined) {
 throw '';
 }
 return;
 }
 catch (e) {
 throw nextOrObserverMessage;
 }
 }
 var specs = [
 fbsArgs.stringSpec(typeValidator),
 fbsArgs.looseObjectSpec(nextOrObserverValidator, true),
 fbsArgs.nullFunctionSpec(true),
 fbsArgs.nullFunctionSpec(true)
 ];
 fbsArgs.validate('on', specs, arguments);
 var self = this;
 function makeBinder(specs) {
 function binder(nextOrObserver, error, opt_complete) {
 if (specs !== null) {
 fbsArgs.validate('on', specs, arguments);
 }
 var observer = new Observer(nextOrObserver, error, completed);
 self.addObserver_(observer);
 return function () {
 self.removeObserver_(observer);
 };
 }
 return binder;
 }
 function binderNextOrObserverValidator(p) {
 if (p === null) {
 throw nextOrObserverMessage;
 }
 nextOrObserverValidator(p);
 }
 var binderSpecs = [
 fbsArgs.looseObjectSpec(binderNextOrObserverValidator),
 fbsArgs.nullFunctionSpec(true),
 fbsArgs.nullFunctionSpec(true)
 ];
 var typeOnly = !(typeUtils.isJustDef(nextOrObserver) ||
 typeUtils.isJustDef(error) ||
 typeUtils.isJustDef(completed));
 if (typeOnly) {
 return makeBinder(binderSpecs);
 }
 else {
 return makeBinder(null)(nextOrObserver, error, completed);
 }
 };
 /**
 * This object behaves like a Promise, and resolves with its snapshot data
 * when the upload completes.
 * @param onFulfilled The fulfillment callback. Promise chaining works as normal.
 * @param onRejected The rejection callback.
 */
 UploadTask.prototype.then = function (onFulfilled, onRejected) {
 // These casts are needed so that TypeScript can infer the types of the
 // resulting Promise.
 return this.promise_.then(onFulfilled, onRejected);
 };
 /**
 * Equivalent to calling `then(null, onRejected)`.
 */
 UploadTask.prototype.catch = function (onRejected) {
 return this.then(null, onRejected);
 };
 /**
 * Adds the given observer.
 */
 UploadTask.prototype.addObserver_ = function (observer) {
 this.observers_.push(observer);
 this.notifyObserver_(observer);
 };
 /**
 * Removes the given observer.
 */
 UploadTask.prototype.removeObserver_ = function (observer) {
 fbsArray.remove(this.observers_, observer);
 };
 UploadTask.prototype.notifyObservers_ = function () {
 var _this = this;
 this.finishPromise_();
 var observers = fbsArray.clone(this.observers_);
 observers.forEach(function (observer) {
 _this.notifyObserver_(observer);
 });
 };
 UploadTask.prototype.finishPromise_ = function () {
 if (this.resolve_ !== null) {
 var triggered = true;
 switch (fbsTaskEnums.taskStateFromInternalTaskState(this.state_)) {
 case TaskState.SUCCESS:
 fbsAsync(this.resolve_.bind(null, this.snapshot))();
 break;
 case TaskState.CANCELED:
 case TaskState.ERROR:
 var toCall = this.reject_;
 fbsAsync(toCall.bind(null, this.error_))();
 break;
 default:
 triggered = false;
 break;
 }
 if (triggered) {
 this.resolve_ = null;
 this.reject_ = null;
 }
 }
 };
 UploadTask.prototype.notifyObserver_ = function (observer) {
 var externalState = fbsTaskEnums.taskStateFromInternalTaskState(this.state_);
 switch (externalState) {
 case TaskState.RUNNING:
 case TaskState.PAUSED:
 if (observer.next !== null) {
 fbsAsync(observer.next.bind(observer, this.snapshot))();
 }
 break;
 case TaskState.SUCCESS:
 if (observer.complete !== null) {
 fbsAsync(observer.complete.bind(observer))();
 }
 break;
 case TaskState.CANCELED:
 case TaskState.ERROR:
 if (observer.error !== null) {
 fbsAsync(observer.error.bind(observer, this.error_))();
 }
 break;
 default:
 // TODO(andysoto): assert(false);
 if (observer.error !== null) {
 fbsAsync(observer.error.bind(observer, this.error_))();
 }
 }
 };
 /**
 * Resumes a paused task. Has no effect on a currently running or failed task.
 * @return True if the operation took effect, false if ignored.
 */
 UploadTask.prototype.resume = function () {
 fbsArgs.validate('resume', [], arguments);
 var valid = this.state_ === InternalTaskState.PAUSED ||
 this.state_ === InternalTaskState.PAUSING;
 if (valid) {
 this.transition_(InternalTaskState.RUNNING);
 }
 return valid;
 };
 /**
 * Pauses a currently running task. Has no effect on a paused or failed task.
 * @return True if the operation took effect, false if ignored.
 */
 UploadTask.prototype.pause = function () {
 fbsArgs.validate('pause', [], arguments);
 var valid = this.state_ === InternalTaskState.RUNNING;
 if (valid) {
 this.transition_(InternalTaskState.PAUSING);
 }
 return valid;
 };
 /**
 * Cancels a currently running or paused task. Has no effect on a complete or
 * failed task.
 * @return True if the operation took effect, false if ignored.
 */
 UploadTask.prototype.cancel = function () {
 fbsArgs.validate('cancel', [], arguments);
 var valid = this.state_ === InternalTaskState.RUNNING ||
 this.state_ === InternalTaskState.PAUSING;
 if (valid) {
 this.transition_(InternalTaskState.CANCELING);
 }
 return valid;
 };
 return UploadTask;
}());
export { UploadTask };

//# sourceMappingURL=task.js.map



//////////////////
// WEBPACK FOOTER
// ../storage/dist/esm/src/task.js
// module id = null
// module chunks = 